"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const solutionPoints = [
{
  icon: "PhotoIcon",
  label: "Rich media galleries for every item",
  color: "text-accent-purple"
},
{
  icon: "PencilSquareIcon",
  label: "Capture signatures, provenance & authenticity",
  color: "text-accent-gold"
},
{
  icon: "MapPinIcon",
  label: "Interactive markers highlight special details",
  color: "text-accent-cyan"
},
{
  icon: "LockClosedIcon",
  label: "Your data, your way — cloud or self-hosted",
  color: "text-accent-emerald"
}];


export default function ProblemSolutionSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal-hidden").forEach((el, i) => {
              setTimeout(() => el.classList.add("revealed"), i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 md:py-32 px-6 md:px-8 bg-bg-primary relative overflow-hidden"
      aria-label="Problem and solution">
      
      <div className="max-w-container mx-auto">
        {/* Section Label */}
        <div className="text-center mb-16 reveal-hidden">
          <span className="font-mono text-[11px] text-accent-crimson uppercase tracking-[0.3em]">
            The Problem · The Solution
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Problem Side */}
          <div className="reveal-hidden relative rounded-3xl overflow-hidden img-zoom group">
            <AppImage
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1c3fab8f8-1771515380271.png"
              alt="Collectibles hidden in storage boxes, forgotten and inaccessible"
              width={800}
              height={600}
              className="w-full h-72 md:h-96 object-cover grayscale brightness-50" />
            
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-end p-8">
              <span className="font-mono text-[10px] text-gray-600 uppercase tracking-widest mb-3">
                Before TapTale
              </span>
              <h3 className="font-serif text-3xl md:text-4xl font-light text-white/60 italic leading-tight">
                Hidden away.
                <br />
                Forgotten stories.
                <br />
                <span className="text-gray-600">Unanswered questions.</span>
              </h3>
            </div>
          </div>

          {/* Solution Side */}
          <div className="reveal-hidden stagger-2 relative rounded-3xl overflow-hidden img-zoom group border border-accent-crimson/20 glow-crimson">
            <AppImage
              src="https://img.rocket.new/generatedImages/rocket_gen_img_115c4e579-1771363109624.png"
              alt="Vibrant collectible displayed in TapTale interactive viewer with glowing markers"
              width={800}
              height={600}
              className="w-full h-72 md:h-96 object-cover brightness-75" />
            
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-accent-crimson/10 via-transparent to-accent-purple/10 pointer-events-none" />

            <div className="absolute inset-0 flex flex-col justify-end p-8">
              <span className="font-mono text-[10px] text-accent-crimson uppercase tracking-widest mb-3">
                After TapTale
              </span>
              <h3 className="font-serif text-3xl md:text-4xl font-light text-white italic leading-tight mb-6">
                Living memories.
                <br />
                Instant access.
                <br />
                <span className="gradient-text-crimson">Stories that last.</span>
              </h3>

              {/* Solution Points */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {solutionPoints.map((point) =>
                <div
                  key={point.label}
                  className="flex items-start gap-2 glass-card-dark rounded-xl p-3">
                  
                    <Icon
                    name={point.icon as any}
                    size={14}
                    className={`${point.color} flex-shrink-0 mt-0.5`} />
                  
                    <span className="text-gray-300 text-xs leading-snug">
                      {point.label}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}